import { useState, useRef, useEffect } from 'react';
import { mockTodos } from '@/mocks/userData';

type Todo = { id: string; text: string; done: boolean };

export default function TodoList() {
  const [todos, setTodos] = useState<Todo[]>(mockTodos);
  const [showInput, setShowInput] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showInput) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [showInput]);

  const toggle = (id: string) => {
    setTodos(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));
  };

  const deleteTodo = (id: string) => {
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const addTodo = () => {
    const text = inputVal.trim();
    if (!text) return;
    const newTodo: Todo = {
      id: Date.now().toString(),
      text,
      done: false,
    };
    setTodos(prev => [...prev, newTodo]);
    setInputVal('');
    setShowInput(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') addTodo();
    if (e.key === 'Escape') {
      setShowInput(false);
      setInputVal('');
    }
  };

  return (
    <div className="px-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm" style={{ color: '#3D3D3D' }}>今日待办</span>
        <button
          onClick={() => setShowInput(v => !v)}
          className="text-xs cursor-pointer whitespace-nowrap flex items-center gap-1 transition-all active:scale-95"
          style={{ color: '#C5B9D8' }}
        >
          <i className={`${showInput ? 'ri-close-line' : 'ri-add-line'}`} style={{ fontSize: 14 }} />
          {showInput ? '取消' : '添加'}
        </button>
      </div>

      {/* 输入框 */}
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: showInput ? 64 : 0, opacity: showInput ? 1 : 0 }}
      >
        <div
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-2xl"
          style={{
            background: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(197,185,216,0.35)',
          }}
        >
          <i className="ri-add-circle-line flex-shrink-0" style={{ color: '#C5B9D8', fontSize: 16 }} />
          <input
            ref={inputRef}
            type="text"
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="输入待办事项…"
            maxLength={40}
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: '#3D3D3D' }}
          />
          <button
            onClick={addTodo}
            disabled={!inputVal.trim()}
            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-all active:scale-90 disabled:opacity-30"
            style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' }}
          >
            <i className="ri-check-line text-white" style={{ fontSize: 12 }} />
          </button>
        </div>
      </div>

      {/* 待办列表 */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.7)',
        }}
      >
        {todos.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 gap-1">
            <i className="ri-checkbox-circle-line" style={{ color: '#C5B9D8', fontSize: 28 }} />
            <span className="text-xs" style={{ color: '#B0B0B0' }}>今天还没有待办，点上方添加吧</span>
          </div>
        )}
        {todos.map((todo, i) => (
          <div key={todo.id} className="group">
            <div className="flex items-center gap-3 px-4 py-3">
              <button
                onClick={() => toggle(todo.id)}
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-all active:scale-90"
                style={{
                  border: todo.done ? 'none' : '1.5px solid rgba(181,198,224,0.6)',
                  background: todo.done ? 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' : 'transparent',
                }}
              >
                {todo.done && <i className="ri-check-line text-white" style={{ fontSize: 11 }} />}
              </button>
              <span
                className="flex-1 text-sm"
                style={{
                  color: todo.done ? '#B0B0B0' : '#3D3D3D',
                  textDecoration: todo.done ? 'line-through' : 'none',
                }}
              >
                {todo.text}
              </span>
              <button
                onClick={() => deleteTodo(todo.id)}
                className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity active:scale-90"
                style={{ color: '#D8C5C5' }}
              >
                <i className="ri-close-line" style={{ fontSize: 14 }} />
              </button>
            </div>
            {i < todos.length - 1 && (
              <div className="mx-4 h-px" style={{ background: 'rgba(181,198,224,0.15)' }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
